import { Input, Form, Button, notification } from "antd";
import type { FormProps } from "antd";
import { Mail, Lock, ArrowRight, Loader2, CheckCircle } from "lucide-react";
import { useState } from "react";
import { getUserManagementService } from "../../backend";
import { useNavigate, useParams } from "react-router";
import { trackUserResetPassword } from "../../backend/tracking/authentication";
import MicroticaLogoColor from "../../assets/logo-color.svg";
import { Link } from "react-router-dom";
import AuthVisualPanel from "../../components/auth/AuthVisualPanel";

type RequiredMark = FormProps['requiredMark'];

export default function ResetPassword() {
    const [form] = Form.useForm();
    const { email, code } = useParams();
    const navigate = useNavigate();
    const [requiredMark] = useState<RequiredMark>('optional');
    const [loading, setLoading] = useState(false);
    const [passwordSet, setPasswordSet] = useState(false);

    async function handleResetPassword(values: { email: string; password: string; }) {
        try {
            setLoading(true);
            await getUserManagementService().confirmUserForgotPassword({
                username: email!,
                password: values.password,
                code: code!
            });
            setPasswordSet(true);
            notification.success({
                message: "New password set",
                description: "Your new password was successfully set."
            });
            trackUserResetPassword(email!);
        } catch (error: any) {
            if (error.response.data.code === "ExpiredCodeException") {
                notification.warning({
                    message: "Unable to set new password",
                    description: <>
                        The password reset is <b>valid for 1 hour</b>. Please <a href="/auth/reset-password">request a new reset link</a>.
                    </>
                });
            } else if (error.response.data.code === "LimitExceededException") {
                notification.error({
                    message: "Unable to set new password",
                    description: error.response.data.message
                });
            } else {
                notification.error({
                    message: "Unable to set new password",
                    description: "Please contact support."
                });
            }
        } finally {
            setLoading(false);
        }
    }

    function handleGotoLogin() {
        navigate("/auth/login");
    }

    return (
        <div className="login-container">
            {/* Left side - Visual/Brand section */}
            <AuthVisualPanel />

            {/* Right side - Set new password form */}
            <div className="login-form-section">
                <div className="login-form-wrapper">
                    {/* Mobile logo */}
                    <div className="login-mobile-brand">
                        <Link to="/">
                            <img src={MicroticaLogoColor} alt="Microtica" />
                        </Link>
                    </div>

                    {passwordSet ? (
                        // Password set success state
                        <div className="signup-success">
                            <div className="signup-success-icon">
                                <CheckCircle size={48} />
                            </div>
                            <h2 className="signup-success-title">Password updated!</h2>
                            <p className="signup-success-text">
                                Your password has been successfully changed. 
                                You can now sign in with your new password.
                            </p>
                            <Button 
                                type="primary" 
                                size="large"
                                block
                                className="login-submit-btn"
                                onClick={handleGotoLogin}
                            >
                                <span>Continue to sign in</span>
                                <ArrowRight size={18} className="login-submit-arrow" />
                            </Button>
                        </div>
                    ) : (
                        // Set new password form
                        <>
                            <div className="login-form-header">
                                <h2 className="login-form-title">Set new password</h2>
                                <p className="login-form-subtitle">
                                    Create a new password for your account.
                                </p>
                            </div>

                            <Form
                                form={form}
                                layout="vertical"
                                requiredMark={requiredMark}
                                onFinish={handleResetPassword}
                                className="login-form"
                            >
                                <Form.Item
                                    name="email"
                                    required
                                >
                                    <Input 
                                        placeholder="Email address" 
                                        prefix={<Mail size={18} strokeWidth={1.5} />}
                                        className="login-input"
                                        size="large"
                                        defaultValue={email}
                                        disabled
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="password"
                                    required
                                    rules={[
                                        { required: true, message: `Please enter your new password` },
                                        { min: 8, message: "Password must be at least 8 characters" }
                                    ]}
                                >
                                    <Input.Password 
                                        placeholder="New password" 
                                        prefix={<Lock size={18} strokeWidth={1.5} />}
                                        className="login-input"
                                        size="large"
                                    />
                                </Form.Item>

                                <Button 
                                    htmlType="submit" 
                                    type="primary" 
                                    block 
                                    size="large"
                                    disabled={loading}
                                    className={`login-submit-btn ${loading ? 'login-submit-btn-loading' : ''}`}
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 size={18} className="animate-spin" />
                                            <span>Setting password...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span>Set new password</span>
                                            <ArrowRight size={18} className="login-submit-arrow" />
                                        </>
                                    )}
                                </Button>
                            </Form>

                            <div className="login-signup-link">
                                Remember your password? <Link to="/auth/login">Sign in</Link>
                            </div>
                        </>
                    )}
                </div>

                <div className="login-footer">
                    <span>© {new Date().getFullYear()} Microtica. All rights reserved.</span>
                </div>
            </div>
        </div>
    )
}
